"use client";

import { c as _c } from "react/compiler-runtime";
import FolderOpen from "@mui/icons-material/FolderOpen";
import MenuIcon from "@mui/icons-material/Menu";
import Settings from "@mui/icons-material/Settings";
import { alpha, AppBar, type AppBarProps, Avatar, Box, Button, Container, IconButton, Menu, MenuItem, Link as MuiLink, Skeleton, Stack, Toolbar, type ToolbarProps, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { signIn, useSession } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";
import React, { type MouseEvent, useState } from "react";
import { MOBILE_APPBAR_HEIGHT } from "#/features/appBar/constants";
import { selectIsAppBarScrolled } from "#/features/appBar/model/appBarSlice";
import { SidePanel } from "#/features/menuSidePanel/ui/SidePanel";
import { useMobilePlaygroundView } from "#/features/playground/hooks/useMobilePlaygroundView";
import { useProjectBrowserContext } from "#/features/project/ui/ProjectBrowser/ProjectBrowserContext";
import { appFontStackDisplay } from "#/shared/fonts/fontVariables";
import { useProfileImageUploader } from "#/shared/hooks";
import { useI18nContext } from "#/shared/hooks";
import { useHasMounted } from "#/shared/hooks/useHasMounted";
import { useMobileLayout } from "#/shared/hooks/useMobileLayout";
import { getImageUrl } from "#/shared/lib";
import { useAppSelector } from "#/store/hooks";
const AVATAR_PLACEHOLDER = "/avatars/placeholder.png";
export type MainAppBarProps = {
  appBarVariant?: AppBarProps["variant"];
  toolbarVariant?: ToolbarProps["variant"];
  position?: AppBarProps["position"];
};
const LogoBrand: React.FC<{
  alt: string;
  showName?: boolean;
  logoSize?: number;
}> = t0 => {
  const $ = _c(10);
  const {
    alt,
    showName: t1,
    logoSize: t2
  } = t0;
  const showName = t1 === undefined ? true : t1;
  const logoSize = t2 === undefined ? 32 : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      display: "flex",
      alignItems: "center",
      textDecoration: "none",
      color: "inherit",
      mr: 1
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      display: "flex",
      alignItems: "center",
      gap: 0.5
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== alt || $[3] !== logoSize) {
    t5 = <Image alt={alt} src="/android-chrome-192x192.png" width={logoSize} height={logoSize} />;
    $[2] = alt;
    $[3] = logoSize;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== showName) {
    t6 = showName && <Typography variant="h6" noWrap={true} sx={{
      fontFamily: appFontStackDisplay,
      fontWeight: 700,
      letterSpacing: "-0.04em"
    }}>dStruct</Typography>;
    $[5] = showName;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t5 || $[8] !== t6) {
    t7 = <MuiLink component={Link} href="/" sx={t3}><Box sx={t4}>{t5}{t6}</Box></MuiLink>;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
};
export const MainAppBar: React.FC<MainAppBarProps> = t0 => {
  const $ = _c(74);
  const {
    appBarVariant: t1,
    toolbarVariant: t2,
    position: t3
  } = t0;
  const appBarVariant = t1 === undefined ? "elevation" : t1;
  const toolbarVariant = t2 === undefined ? "dense" : t2;
  const position = t3 === undefined ? "sticky" : t3;
  const router = useRouter();
  const currentPath = router.pathname;
  const theme = useTheme();
  const isMobileLayout = useMobileLayout();
  let t4;
  if ($[0] !== theme.breakpoints) {
    t4 = theme.breakpoints.down("lg");
    $[0] = theme.breakpoints;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const useCompactNav = useMediaQuery(t4);
  const {
    LL
  } = useI18nContext();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const hasMounted = useHasMounted();
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [anchorElNav, setAnchorElNav] = useState(null);
  const {
    openBrowser
  } = useProjectBrowserContext();
  const {
    currentView
  } = useMobilePlaygroundView();
  const isScrolled = useAppSelector(selectIsAppBarScrolled);
  const session = useSession();
  let t5;
  if ($[2] !== currentPath) {
    t5 = currentPath.startsWith("/playground");
    $[2] = currentPath;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const isPlayground = t5;
  const useMobilePlayground = isMobileLayout && isPlayground;
  const pages = [{
    name: LL.PLAYGROUND(),
    href: "/playground"
  }, {
    name: LL.DAILY_PROBLEM_NAV(),
    href: "/daily"
  }] as const;
  useProfileImageUploader(session);
  let t6;
  if ($[4] !== LL || $[5] !== enqueueSnackbar || $[6] !== useMobilePlayground) {
    t6 = async () => {
      ;
      try {
        await signIn();
      } catch (t7) {
        const error = t7;
        if (useMobilePlayground) {
          const message = error instanceof Error ? error.message : LL.SIGN_IN_FAILED();
          enqueueSnackbar(message, {
            variant: "error"
          });
        }
      }
    };
    $[4] = LL;
    $[5] = enqueueSnackbar;
    $[6] = useMobilePlayground;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleSignIn = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = event => {
      setAnchorElNav(event.currentTarget);
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleOpenNavMenu = t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setAnchorElNav(null);
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const handleCloseNavMenu = t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setIsSidePanelOpen(true);
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const handleOpenUserMenu = t9;
  const toolbarHeight = useMobilePlayground ? MOBILE_APPBAR_HEIGHT : 56;
  const isCompact = useCompactNav;
  let t10;
  if ($[11] !== isSidePanelOpen) {
    t10 = <SidePanel isOpen={isSidePanelOpen} setIsOpen={setIsSidePanelOpen} />;
    $[11] = isSidePanelOpen;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const T0 = AppBar;
  const t11 = 0;
  const t12 = "transparent";
  const t13 = isScrolled ? "blur(20px)" : "blur(0px)";
  let t14;
  if ($[13] !== isScrolled || $[14] !== theme.appDesign) {
    t14 = isScrolled ? `1px solid ${alpha(theme.appDesign.outline, 0.18)}` : "1px solid transparent";
    $[13] = isScrolled;
    $[14] = theme.appDesign;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== t13 || $[17] !== t14 || $[18] !== theme.palette.text.primary) {
    t15 = {
      paddingTop: "env(safe-area-inset-top, 0px)",
      transition: "all .2s",
      backdropFilter: t13,
      backgroundColor: "transparent",
      boxShadow: "none",
      borderBottom: t14,
      color: theme.palette.text.primary
    };
    $[16] = t13;
    $[17] = t14;
    $[18] = theme.palette.text.primary;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  const T1 = Container;
  const t16 = "xl";
  const T2 = Toolbar;
  const t17 = true;
  let t18;
  if ($[20] !== useMobilePlayground) {
    t18 = useMobilePlayground ? 1 : {
      xs: 1.25,
      md: 0
    };
    $[20] = useMobilePlayground;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  let t19;
  if ($[22] !== t18 || $[23] !== toolbarHeight) {
    t19 = {
      height: toolbarHeight,
      px: t18
    };
    $[22] = t18;
    $[23] = toolbarHeight;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] !== LL) {
    t20 = LL.DSTRUCT_LOGO();
    $[25] = LL;
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  const t21 = !useCompactNav;
  const t22 = isCompact ? 24 : 32;
  let t23;
  if ($[27] !== t20 || $[28] !== t21 || $[29] !== t22) {
    t23 = <LogoBrand alt={t20} showName={t21} logoSize={t22} />;
    $[27] = t20;
    $[28] = t21;
    $[29] = t22;
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  const t24 = useCompactNav && <Box sx={{
    flexGrow: 1,
    display: "flex"
  }}><IconButton size="large" aria-label={LL.CURRENT_USER_ACCOUNT()} aria-controls="menu-appbar" aria-haspopup="true" onClick={handleOpenNavMenu} color="inherit"><MenuIcon /></IconButton><Menu id="menu-appbar" anchorEl={anchorElNav} anchorOrigin={{
      vertical: "bottom",
      horizontal: "left"
    }} keepMounted={true} transformOrigin={{
      vertical: "top",
      horizontal: "left"
    }} open={Boolean(anchorElNav)} onClose={handleCloseNavMenu} sx={{
      display: "block"
    }}>{pages.map(page => <MenuItem key={page.href} component={Link} href={page.href} onClick={handleCloseNavMenu}><Typography textAlign="center">{page.name}</Typography></MenuItem>)}</Menu></Box>;
  const t25 = !useCompactNav && <Box sx={{
    flexGrow: 1,
    gap: 1,
    display: "flex"
  }}>{pages.map(page_0 => <Button key={page_0.href} component={Link} href={page_0.href} color="inherit" sx={{
      backgroundColor: page_0.href && currentPath.startsWith(page_0.href) ? alpha(theme.appDesign.surfaceHighest, 0.9) : "transparent",
      border: page_0.href && currentPath.startsWith(page_0.href) ? `1px solid ${alpha(theme.appDesign.outline, 0.22)}` : "1px solid transparent",
      "&:hover": {
        backgroundColor: alpha(theme.appDesign.surfaceHigh, 0.82),
        borderColor: alpha(theme.appDesign.outline, 0.18)
      }
    }}>{page_0.name}</Button>)}</Box>;
  let t26;
  if ($[31] !== useCompactNav) {
    t26 = useCompactNav && <Box sx={{
      flex: 1
    }} />;
    $[31] = useCompactNav;
    $[32] = t26;
  } else {
    t26 = $[32];
  }
  const t27 = isCompact ? 0.5 : 1;
  let t28;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      flexGrow: 0
    };
    $[33] = t28;
  } else {
    t28 = $[33];
  }
  let t29;
  if ($[34] !== LL || $[35] !== currentView || $[36] !== isCompact || $[37] !== openBrowser || $[38] !== useMobilePlayground) {
    t29 = (!useMobilePlayground || currentView !== "browse") && <Tooltip title={LL.PROJECT_BROWSER()} arrow={true}><IconButton size={isCompact ? "small" : "medium"} onClick={openBrowser} color="inherit" aria-label={LL.PROJECT_BROWSER()}><FolderOpen fontSize={isCompact ? "small" : "medium"} /></IconButton></Tooltip>;
    $[34] = LL;
    $[35] = currentView;
    $[36] = isCompact;
    $[37] = openBrowser;
    $[38] = useMobilePlayground;
    $[39] = t29;
  } else {
    t29 = $[39];
  }
  let t30;
  if ($[40] !== LL || $[41] !== handleOpenUserMenu || $[42] !== handleSignIn || $[43] !== hasMounted || $[44] !== isCompact || $[45] !== session || $[46] !== useMobilePlayground) {
    t30 = useMobilePlayground && !hasMounted || session.status === "loading" ? <Skeleton variant="circular" animation="wave" height={isCompact ? 32 : 40} width={isCompact ? 32 : 40} /> : <>{session.status === "unauthenticated" && <Button title={LL.SIGN_IN()} color="inherit" size={isCompact ? "small" : "medium"} onClick={handleSignIn} sx={isCompact ? {
        fontSize: "0.75rem",
        minWidth: "auto",
        px: 1
      } : undefined}>{LL.SIGN_IN()}</Button>}<Tooltip title={LL.OPEN_OPTIONS()} arrow={true}><IconButton size={isCompact ? "small" : "medium"} onClick={handleOpenUserMenu}>{session.data ? <Avatar sx={isCompact ? {
            width: 28,
            height: 28
          } : undefined}><Image src={getImageUrl(session.data.user.bucketImage || AVATAR_PLACEHOLDER)} alt={`${session.data.user.name} avatar`} referrerPolicy="no-referrer" crossOrigin="anonymous" width={isCompact ? 28 : 40} height={isCompact ? 28 : 40} /></Avatar> : <Settings fontSize={isCompact ? "small" : "medium"} />}</IconButton></Tooltip></>;
    $[40] = LL;
    $[41] = handleOpenUserMenu;
    $[42] = handleSignIn;
    $[43] = hasMounted;
    $[44] = isCompact;
    $[45] = session;
    $[46] = useMobilePlayground;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  let t31;
  if ($[48] !== t27 || $[49] !== t29 || $[50] !== t30) {
    t31 = <Stack direction="row" spacing={t27} alignItems="center" sx={t28}>{t29}{t30}</Stack>;
    $[48] = t27;
    $[49] = t29;
    $[50] = t30;
    $[51] = t31;
  } else {
    t31 = $[51];
  }
  let t32;
  if ($[52] !== T2 || $[53] !== t19 || $[54] !== t23 || $[55] !== t24 || $[56] !== t25 || $[57] !== t26 || $[58] !== t31 || $[59] !== toolbarVariant) {
    t32 = <T2 disableGutters={t17} variant={toolbarVariant} sx={t19}>{t23}{t24}{t25}{t26}{t31}</T2>;
    $[52] = T2;
    $[53] = t19;
    $[54] = t23;
    $[55] = t24;
    $[56] = t25;
    $[57] = t26;
    $[58] = t31;
    $[59] = toolbarVariant;
    $[60] = t32;
  } else {
    t32 = $[60];
  }
  let t33;
  if ($[61] !== T1 || $[62] !== t32 || $[63] !== useMobilePlayground) {
    t33 = <T1 maxWidth={t16} disableGutters={useMobilePlayground}>{t32}</T1>;
    $[61] = T1;
    $[62] = t32;
    $[63] = useMobilePlayground;
    $[64] = t33;
  } else {
    t33 = $[64];
  }
  let t34;
  if ($[65] !== T0 || $[66] !== appBarVariant || $[67] !== position || $[68] !== t15 || $[69] !== t33) {
    t34 = <T0 position={position} elevation={t11} variant={appBarVariant} color={t12} sx={t15}>{t33}</T0>;
    $[65] = T0;
    $[66] = appBarVariant;
    $[67] = position;
    $[68] = t15;
    $[69] = t33;
    $[70] = t34;
  } else {
    t34 = $[70];
  }
  let t35;
  if ($[71] !== t10 || $[72] !== t34) {
    t35 = <>{t10}{t34}</>;
    $[71] = t10;
    $[72] = t34;
    $[73] = t35;
  } else {
    t35 = $[73];
  }
  return t35;
};